/**
 * BALANCE stage. The tilt is the mode's whole identity, so the board really
 * rotates — up to `TUNING.balance.maxTiltDegrees` about the beam it rides on.
 * That cannot fit inside SOLO's three-column shell (at full tilt the top-right
 * corner used to reach x ≈ 579 against a right column starting at 500), so
 * BALANCE gets its own stage the way TWIN already has one.
 *
 * A 24px cell centres a 240 × 480 field on the canvas, the side columns are
 * gone, and the HUD flattens into a horizontal band above the topple line.
 * Swept box across the full ±8° range: x 148–534, y 120–641.
 *
 * Three devices carry the danger, in ascending order of alarm: the full-bleed
 * topple line the high corner climbs toward, the counterweight towers, and the
 * magenta well frame past `dangerRisk`.
 */

import {
  Phase,
  TUNING,
  type BalanceState,
  type GameState,
  type LineClearEvent,
} from '../core/game.ts';
import { PIECE_COLORS } from '../core/piece.ts';
import type { Cell } from '../core/board.ts';
import {
  Painter,
  drawMusicChip,
  drawSoundChip,
  drawThemeChip,
  drawVolumeChip,
  type VolumeLevel,
} from './painter.ts';
import { HitList } from './hits.ts';
import type { Surface } from './surface.ts';
import { drawMiniPiece } from './pieces.ts';
import { collapseProgress, statusOf, type GameView } from './canvas.ts';
import { ClearImpact } from './clearImpact.ts';
import {
  drawGameOverOverlay,
  drawPauseOverlay,
  drawReadyOverlay,
  scrim,
  type OverlayBox,
} from './overlays.ts';
import {
  BALANCE_STAGE as S,
  CANVAS_HEIGHT,
  CANVAS_WIDTH,
  LAYOUT,
  TYPE,
  activeShell,
  faces,
  formatCounter,
  formatScore,
  onBlock,
  palette,
} from './theme.ts';

const HEADER_BASELINE = LAYOUT.pad + 8;
const RULE_Y = HEADER_BASELINE + 10;
/** Response rate for the board's critically damped tilt spring. */
const TILT_SPRING_RATE = 11;
const TILT_SETTLE_EPSILON = 0.001;

/** Everything below the header rule dims together when play stops. */
const OVERLAY_BOX: OverlayBox = {
  x: 0,
  y: RULE_Y + 3,
  w: CANVAS_WIDTH,
  h: CANVAS_HEIGHT - RULE_Y - 3,
};

export class BalanceRenderer {
  readonly hits = new HitList();
  private readonly painter: Painter;
  private levelFlashMs = 0;
  private tiltDegrees = 0;
  private tiltVelocity = 0;
  private readonly clearImpact = new ClearImpact();

  constructor(private readonly surface: Surface) {
    this.painter = surface.painter;
  }

  /** Host wires this to `onLevelUp`, as it does for the solo renderer. */
  flashLevelUp(): void {
    this.levelFlashMs = TUNING.levelUpFlashMs;
  }

  showMultiLineClear(state: GameState, event: LineClearEvent): void {
    this.clearImpact.trigger(state, event);
  }

  draw(
    state: GameState,
    view: GameView,
    dtMs: number,
    muted = false,
    volume: VolumeLevel = 'medium',
    music = true,
  ): void {
    this.levelFlashMs = Math.max(0, this.levelFlashMs - dtMs);
    this.clearImpact.update(dtMs);

    this.surface.begin();
    this.hits.clear();

    const balance = state.balanceState();
    const danger = balance.risk > S.dangerRisk;

    this.drawHeader(state, danger, muted, volume, music);
    this.drawBand(state, view, balance, danger);
    this.drawToppleLine();
    if (danger) this.drawCorrection(balance);
    this.drawTowers(state);
    this.drawTiltedBody(state, danger, dtMs);
    this.drawMount();
    this.drawOverlay(state, view);
  }

  // ------------------------------------------------------------------ header

  private drawHeader(
    state: GameState,
    danger: boolean,
    muted: boolean,
    volume: VolumeLevel,
    music: boolean,
  ): void {
    const p = this.painter;
    const c = palette();
    const y = HEADER_BASELINE;
    p.typed('KD-TETRIS', LAYOUT.pad, y, TYPE.brand, c.textLabel);

    // Past the danger threshold the header carries the state too.
    const status =
      danger && state.phase === Phase.Playing
        ? { text: 'TIPPING', color: '#e72f6f' }
        : statusOf(state);
    let right = CANVAS_WIDTH - LAYOUT.pad;
    right -= p.headerChip(status.text, right, y, status.color).w + 8;
    right -= drawThemeChip(p, this.hits, right, y) + 8;
    right -= drawSoundChip(p, this.hits, right, y, muted) + 8;
    right -= drawMusicChip(p, this.hits, right, y, music) + 8;
    right -= drawVolumeChip(p, this.hits, right, y, volume) + 8;
    if (state.phase === Phase.Playing) {
      const box = p.headerChip('PAUSE', right, y, { f: c.panelAlt, textColor: c.textDim });
      this.hits.add('game.pause', box.x, box.y, box.w, box.h);
    }

    p.rule(LAYOUT.pad, RULE_Y, CANVAS_WIDTH - LAYOUT.pad * 2);
  }

  // -------------------------------------------------------- instrument band

  private drawBand(
    state: GameState,
    view: GameView,
    balance: BalanceState,
    danger: boolean,
  ): void {
    const p = this.painter;
    const c = palette();
    const band = S.band;
    const smallLabel = { size: 9, weight: 700, tracking: 0.2 };

    const identityType = { size: 9, weight: 700, tracking: 0.16 };
    p.typed(
      p.fit(`${view.playerName} · ${view.mode}`, 220, identityType),
      band.identityX,
      S.toppleLine.labelY + 7,
      identityType,
      c.textFaint,
    );

    p.typed('HOLD', band.hold.x, band.hold.labelY + 8, smallLabel, c.textLabel);
    p.bevelRect(band.hold.x, band.hold.y, band.hold.w, band.hold.h, c.panel, 3);
    if (state.hold) {
      drawMiniPiece(
        p,
        state.hold,
        band.hold.x,
        band.hold.y,
        band.hold.w,
        band.hold.h,
        band.miniCell,
        state.canHold ? 1 : 0.3,
      );
    }

    p.typed('NEXT', band.next.x, band.next.labelY + 8, smallLabel, c.textLabel);
    p.bevelRect(band.next.x, band.next.y, band.next.w, band.next.h, c.panel, 3);
    state.preview(TUNING.previewCount).forEach((type, i) => {
      drawMiniPiece(
        p,
        type,
        band.next.x + i * band.next.slot,
        band.next.y,
        band.next.slot,
        band.next.h,
        band.miniCell,
        i === 0 ? 1 : 0.55,
      );
    });

    p.typed('SCORE', band.score.x, band.score.labelY + 8, smallLabel, c.textLabel);
    p.typed(formatCounter(state.score), band.combo.x - 16, band.score.y + 22, TYPE.scoreValue, c.accent, 'right');

    if (state.combo > 0) p.chip(`COMBO ${state.combo}`, band.combo.x, band.combo.y, '#63c9d8');

    const readouts: readonly (readonly [string, string, number, string])[] = [
      ['LINES', formatCounter(state.lines), 700, c.textDim],
      ['LEVEL', formatCounter(state.level), 700, c.textDim],
      ['BEST', formatScore(Math.max(view.best, state.score)), 500, c.textFaint],
    ];
    readouts.forEach(([label, value, weight, color], i) => {
      p.typed(
        `${label} ${value}`,
        band.readouts.rightX,
        band.readouts.ys[i] + 8,
        { size: 9, weight, tracking: 0.16 },
        color,
        'right',
      );
    });

    // The gauge reading, next to the line it is read against.
    const risk = Math.round(Math.min(1, balance.risk) * 100);
    p.typed(
      `TIP RISK ${risk}%`,
      band.readouts.rightX,
      S.toppleLine.labelY + 8,
      { size: 10, weight: 800, tracking: 0.16 },
      danger ? S.dangerInk[activeShell()] : c.textDim,
      'right',
    );
  }

  /** The level reference the tilted board is read against. */
  private drawToppleLine(): void {
    const p = this.painter;
    const line = S.toppleLine;
    const color = line.color[activeShell()];

    // The label names the reference; the risk readout beside it carries the
    // alarm, so this one stays quiet at every risk.
    p.typed(
      'TOPPLE LINE',
      LAYOUT.pad,
      line.labelY + 7,
      { size: 8, weight: 700, tracking: 0.24 },
      palette().textFaint,
    );
    p.fillRect(0, line.y, CANVAS_WIDTH, line.h, color);
    for (const x of line.tickXs) {
      p.fillRect(x, line.y + line.h, line.tickW, line.tickH, color);
    }
  }

  /**
   * Sits in the wedge of empty space the tilt itself opens up — literally the
   * side you have to play into.
   */
  private drawCorrection(balance: BalanceState): void {
    const p = this.painter;
    const b = S.banner;
    const side = balance.offset > 0 ? 'LEFT' : 'RIGHT';
    const cols = Math.max(1, Math.round(Math.abs(balance.offset)));
    const x = side === 'RIGHT' ? CANVAS_WIDTH - b.x - b.w : b.x;
    p.bevelRect(x, b.y, b.w, b.h, faces('#e72f6f'), 4);
    p.typed(
      `CORRECT ${side} · ${cols} COL${cols === 1 ? '' : 'S'}`,
      x + b.w / 2,
      b.y + b.h / 2 + 10 * 0.36,
      { size: 10, weight: 800, tracking: 0.14 },
      onBlock('#e72f6f').text,
      'center',
    );
  }

  /**
   * One block per four settled cells either side of the pivot. The label and
   * count sit directly above each tower, so the two headers end up at different
   * heights — the imbalance is legible before you read a number.
   */
  private drawTowers(state: GameState): void {
    const p = this.painter;
    const c = palette();
    const t = S.towers;
    const shell = activeShell();
    const cells = state.board.snapshot();
    const width = TUNING.board.width;
    const half = width / 2;

    let left = 0;
    let right = 0;
    for (let y = TUNING.board.bufferHeight; y < TUNING.board.height; y++) {
      for (let x = 0; x < width; x++) {
        const cell: Cell = cells[y * width + x];
        if (!cell) continue;
        if (x < half) left++;
        else right++;
      }
    }

    const sides: readonly {
      x: number;
      color: string;
      ink: Record<'night' | 'day', string>;
      label: string;
      count: number;
    }[] = [
      { ...t.left, label: 'LOAD L', count: left },
      { ...t.right, label: 'LOAD R', count: right },
    ];
    for (const side of sides) {
      const blocks = Math.round(side.count / t.cellsPerBlock);
      const maxBlocks = Math.floor((t.baseY - 176) / t.pitch) + 1;
      const visibleBlocks = Math.min(blocks, maxBlocks);
      for (let i = 0; i < visibleBlocks; i++) {
        p.bevelRect(side.x, t.baseY - i * t.pitch, t.block, t.block, faces(side.color), 3);
      }
      const topY = t.baseY - Math.max(0, visibleBlocks - 1) * t.pitch;
      p.typed(side.label, side.x, topY - 22, { size: 8, weight: 700, tracking: 0.2 }, c.textLabel);
      p.typed(
        side.count.toString(),
        side.x,
        topY - 4,
        { size: 18, weight: 800, tracking: 0.02 },
        side.ink[shell],
      );
    }
  }

  // ------------------------------------------------------------- the balance

  /** Board and beam are one body: same rotation, same pivot. */
  private drawTiltedBody(state: GameState, danger: boolean, dtMs: number): void {
    const ctx = this.surface.ctx;
    const balance = state.balanceState();
    const targetTilt =
      Math.sign(balance.offset) * Math.min(1, balance.risk) * TUNING.balance.maxTiltDegrees;
    const tilt = this.updateTilt(targetTilt, dtMs);

    ctx.save();
    ctx.translate(S.pivot.x, S.pivot.y);
    ctx.rotate((tilt * Math.PI) / 180);
    ctx.translate(-S.pivot.x, -S.pivot.y);
    const shake = this.clearImpact.shakeOffset();
    ctx.translate(shake.x, shake.y);
    this.drawField(state, danger);
    this.painter.bevelRect(S.beam.x, S.beam.y, S.beam.w, S.beam.h, faces(S.beam.color), 3);
    this.clearImpact.draw(this.painter, S.field.x, S.field.y, S.cell);
    ctx.restore();
  }

  /** Smooth abrupt load changes without changing the simulation's balance state. */
  private updateTilt(target: number, dtMs: number): number {
    const dt = Math.min(Math.max(dtMs, 0), TUNING.maxStepMs) / 1000;
    if (dt === 0) return this.tiltDegrees;

    const offset = this.tiltDegrees - target;
    const impulse = this.tiltVelocity + TILT_SPRING_RATE * offset;
    const decay = Math.exp(-TILT_SPRING_RATE * dt);

    this.tiltDegrees = target + (offset + impulse * dt) * decay;
    this.tiltVelocity = (this.tiltVelocity - TILT_SPRING_RATE * impulse * dt) * decay;

    if (
      Math.abs(this.tiltDegrees - target) < TILT_SETTLE_EPSILON &&
      Math.abs(this.tiltVelocity) < TILT_SETTLE_EPSILON
    ) {
      this.tiltDegrees = target;
      this.tiltVelocity = 0;
    }
    return this.tiltDegrees;
  }

  private drawField(state: GameState, danger: boolean): void {
    const ctx = this.surface.ctx;
    const p = this.painter;
    const c = palette();
    const { x, y, w, h } = S.field;
    const f = S.frame;
    const flashing = this.levelFlashMs > 0;
    const flashAlpha = this.levelFlashMs / TUNING.levelUpFlashMs;

    // The ring alone recolours past the danger threshold; the interior stays
    // `field`, or the 5%-alpha ghost cells stain.
    const frameFaces = flashing
      ? c.accentFaces
      : danger
        ? S.dangerFrame[activeShell()]
        : c.well;
    p.bevelRect(x - f, y - f, w + f * 2, h + f * 2, frameFaces, f);
    p.fillRect(x, y, w, h, c.field);

    ctx.save();
    ctx.beginPath();
    ctx.rect(x, y, w, h);
    ctx.clip();

    const cell = S.cell;
    for (let r = 0; r < TUNING.board.visibleHeight; r++) {
      for (let col = 0; col < TUNING.board.width; col++) {
        p.blockCell(x + col * cell, y + r * cell, cell, c.cellEmpty);
      }
    }

    this.drawStack(state);
    if (!state.clearAnim) {
      for (const g of state.ghostCells()) {
        p.blockCell(x + g.x * cell, y + (g.y - TUNING.board.bufferHeight) * cell, cell, c.ghost);
      }
      const type = state.active?.type;
      if (type) {
        for (const a of state.activeCells()) {
          p.blockCell(
            x + a.x * cell,
            y + (a.y - TUNING.board.bufferHeight) * cell,
            cell,
            PIECE_COLORS[type],
          );
        }
      }
    }

    if (flashing) {
      ctx.globalAlpha = flashAlpha;
      p.fillRect(x, y, w, h, c.levelFlash);
      ctx.globalAlpha = 1;
    }
    ctx.restore();
  }

  private drawStack(state: GameState): void {
    const p = this.painter;
    const { x, y } = S.field;
    const cell = S.cell;
    const cells = state.board.snapshot();
    const anim = state.clearAnim;
    const clearing = new Set(anim?.rows ?? []);
    const flashing =
      anim !== null && anim.elapsedMs < TUNING.lineClearFlashMs;
    const collapse = anim ? collapseProgress(anim) : 0;

    for (let row = TUNING.board.bufferHeight; row < TUNING.board.height; row++) {
      const isClearing = clearing.has(row);
      if (isClearing && !flashing) continue;
      const below = anim && !isClearing
        ? anim.rows.reduce((count, cleared) => count + (cleared > row ? 1 : 0), 0)
        : 0;
      const shift = below * cell * collapse;
      for (let col = 0; col < TUNING.board.width; col++) {
        const cellValue: Cell = cells[row * TUNING.board.width + col];
        if (!cellValue) continue;
        p.blockCell(
          x + col * cell,
          y + (row - TUNING.board.bufferHeight) * cell + shift,
          cell,
          isClearing ? '#ffffff' : PIECE_COLORS[cellValue],
        );
      }
    }
  }

  /**
   * The static machinery under the beam. The mount is one value below the beam
   * so it reads as structure rather than surface, and the floor closes the
   * composition — `rule` measures 1.1:1 on open background and vanishes.
   */
  private drawMount(): void {
    const p = this.painter;
    const fulcrum = S.fulcrum;
    const f = faces(fulcrum.color);
    fulcrum.ys.forEach((y, i) => {
      const w = fulcrum.widths[i];
      p.bevelRect(fulcrum.centerX - w / 2, y, w, fulcrum.h, f, 3);
    });
    p.fillRect(0, S.floor.y, CANVAS_WIDTH, S.floor.h, S.floor.color);
  }

  private drawOverlay(state: GameState, view: GameView): void {
    if (state.phase === Phase.Playing) return;
    scrim(this.painter, OVERLAY_BOX);

    if (state.phase === Phase.Ready) {
      drawReadyOverlay(this.painter, this.hits, OVERLAY_BOX);
      return;
    }
    if (state.phase === Phase.Paused) {
      drawPauseOverlay(this.painter, this.hits, OVERLAY_BOX, state);
      return;
    }
    drawGameOverOverlay(this.painter, this.hits, OVERLAY_BOX, state, view);
  }
}
